import { useEffect } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { callApi } from '../api';
import { useSession } from '../hooks/useSession';

// `end: true` on entries whose path is a prefix of another sidebar entry —
// otherwise NavLink prefix-matches and lights up both rows (e.g. "Imports"
// stays active on /admin/imports/unmatched, "System health" stays active
// on /admin/system/crons).
const NAV = [
  { to: '/admin', label: 'Overview', end: true },
  { to: '/admin/imports', label: 'Imports', badgeKey: 'beli_queue_pending_count', end: true },
  { to: '/admin/users', label: 'Users' },
  { to: '/admin/feedback', label: 'Feedback', badgeKey: 'open_feedback_count' },
  { to: '/admin/waitlist', label: 'Waitlist', badgeKey: 'waitlist_pending_count' },
  { to: '/admin/invites', label: 'Invites' },
  { to: '/admin/system', label: 'System health', end: true },
  { to: '/admin/system/crons', label: 'Crons' },
  { to: '/admin/search', label: 'Search analytics' },
  { to: '/admin/imports/unmatched', label: 'Unmatched' },
];

export function Layout() {
  const {
    session, me, signOut, counts, setCounts, excludeAdmins, setExcludeAdmins,
  } = useSession();

  // One overview poll feeds every sidebar badge AND the Overview page (which
  // reads counts from the same context). 30s is unobtrusive but still
  // surfaces changes in a reasonable window. Errors are swallowed because
  // badges are decorative.
  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      try {
        const data = await callApi('/api/v1/admin/overview', {
          accessToken: session.access_token,
        });
        if (alive) setCounts(data || {});
      } catch {
        /* badges are decorative */
      }
    };
    refresh();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [session.access_token, setCounts]);

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <aside className="w-56 bg-white border-r border-stone-200 p-4 flex flex-col">
        <Link to="/admin" className="text-lg font-semibold text-stone-800">
          Palate Admin
        </Link>
        {me && (
          <p className="text-xs text-stone-500 mb-4 mt-0.5">
            Hi, {me.display_name || me.username || 'admin'}
          </p>
        )}
        {!me && <div className="mb-4" />}
        <nav className="flex-1 space-y-0.5">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center justify-between px-2.5 py-1.5 rounded text-sm ${
                  isActive
                    ? 'bg-stone-800 text-white'
                    : 'text-stone-700 hover:bg-stone-100'
                }`
              }
            >
              <span>{item.label}</span>
              {item.badgeKey && counts[item.badgeKey] > 0 && (
                <span className="text-xs bg-amber-100 text-amber-800 rounded px-1.5">
                  {counts[item.badgeKey]}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>
      <main className="flex-1 overflow-auto">
        {/* Sticky top bar so the toggle + sign-out stay visible no matter
            how far the page scrolls. */}
        <header className="sticky top-0 z-10 bg-stone-50/90 backdrop-blur border-b border-stone-200 px-6 py-3 flex items-center justify-end gap-4">
          <label className="flex items-center gap-2 text-xs text-stone-600 cursor-pointer">
            <input
              type="checkbox"
              checked={excludeAdmins}
              onChange={(e) => setExcludeAdmins(e.target.checked)}
            />
            Exclude admin users
          </label>
          <button
            onClick={signOut}
            className="text-xs text-stone-500 underline"
          >
            Sign out
          </button>
        </header>
        <div className="max-w-5xl p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
